import React, {useState} from 'react';
import {Typography, Popover, Box, Chip, makeStyles, IconButton, Link} from '@material-ui/core';
import {HelpOutline} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
    popover: {
        pointerEvents: 'none',
    },
    paper: {
        padding: theme.spacing(2),
        maxWidth: 500,
        pointerEvents: 'auto',
    },
    helpLink: {
        display: 'inline-flex',
        alignItems: 'center',
        marginTop: theme.spacing(2),
        color: '#838383',
        fontFamily: 'FredokaRegular',
        fontSize: 14,
        cursor: 'pointer',
        '&:hover': {
            color: '#456d69',
            textDecoration: 'underline',
        },
    },
    helpIcon: {
        fontSize: 16,
        marginLeft: 4,
    },
    productItem: {
        marginLeft: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    productName: {
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    reasonText: {
        fontSize: 14,
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(2),
    },
    noFailuresMessage: {
        fontStyle: 'italic',
        color: theme.palette.text.secondary,
    },
}));

type FailureReason =
    | {type: 'simple'; text: string}
    | {type: 'any'; text: string; alternatives: FailureReason[]}
    | {type: 'all'; items: FailureReason[]};

interface ProductFailureReason {
    productCode: string;
    productName: string;
    failureReasons: FailureReason[];
}

interface ProductFailureReasonsPopoverProps {
    failureReasons?: ProductFailureReason[];
    allProducts: any[];
    eligibleProductCodes: string[];
}

// Recursive component to render failure reasons
const RenderFailureReason: React.FC<{reason: FailureReason; indent: number}> = ({reason, indent}) => {
    const marginLeft = indent * 16;

    if (reason.type === 'simple') {
        return (
            <Typography
                variant="caption"
                style={{
                    display: 'block',
                    marginLeft,
                    marginBottom: 2,
                    color: '#666',
                }}
            >
                • {reason.text}
            </Typography>
        );
    }

    if (reason.type === 'any') {
        return (
            <Box style={{marginLeft, marginBottom: 4}}>
                <Typography
                    variant="caption"
                    style={{
                        display: 'block',
                        fontWeight: 500,
                        color: '#444',
                        marginBottom: 2,
                    }}
                >
                    • {reason.text}:
                </Typography>
                {reason.alternatives.map((alt, index) => (
                    <Box key={index} style={{marginLeft: 16, marginBottom: 4}}>
                        {index > 0 && (
                            <Typography
                                variant="caption"
                                style={{
                                    display: 'block',
                                    fontStyle: 'italic',
                                    color: '#888',
                                    marginBottom: 2,
                                }}
                            >
                                OR
                            </Typography>
                        )}
                        <RenderFailureReason reason={alt} indent={0} />
                    </Box>
                ))}
            </Box>
        );
    }

    if (reason.type === 'all') {
        return (
            <Box style={{marginLeft, marginBottom: 2}}>
                {reason.items.map((item, index) => (
                    <Box key={index} style={{marginBottom: index > 0 ? 2 : 0}}>
                        {index > 0 && reason.items.length > 1 && (
                            <Typography
                                variant="caption"
                                style={{
                                    display: 'block',
                                    fontStyle: 'italic',
                                    color: '#888',
                                    marginBottom: 2,
                                    marginLeft: 16,
                                }}
                            >
                                AND
                            </Typography>
                        )}
                        <RenderFailureReason reason={item} indent={0} />
                    </Box>
                ))}
            </Box>
        );
    }

    return null;
};

export const ProductFailureReasonsPopover: React.FC<ProductFailureReasonsPopoverProps> = ({
    failureReasons,
    allProducts,
    eligibleProductCodes,
}) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    // Calculate ineligible products
    const ineligibleProducts = allProducts.filter(
        (product) => !eligibleProductCodes.includes(product.code) && !['LSWIUL', 'LSWTERM'].includes(product.code), // Exclude these from display
    );

    // If no failure reasons data, show generic message
    if (!failureReasons || failureReasons.length === 0) {
        // Only show if there are actually ineligible products
        if (ineligibleProducts.length === 0) {
            return null;
        }

        return (
            <>
                <Link component="button" className={classes.helpLink} onClick={handleClick}>
                    Why are some products missing?
                    <HelpOutline className={classes.helpIcon} />
                </Link>
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    classes={{
                        paper: classes.paper,
                    }}
                >
                    <Box>
                        <Typography variant="subtitle1" style={{fontWeight: 600, marginBottom: 12}}>
                            Products Not Available
                        </Typography>
                        <Typography variant="body2" className={classes.noFailuresMessage}>
                            {ineligibleProducts.length} product{ineligibleProducts.length !== 1 ? 's' : ''} did not meet
                            eligibility requirements based on the client's information.
                        </Typography>
                        <Box mt={2}>
                            <Typography variant="caption" style={{color: '#999'}}>
                                Ineligible products: {ineligibleProducts.map((p) => p.name).join(', ')}
                            </Typography>
                        </Box>
                    </Box>
                </Popover>
            </>
        );
    }

    return (
        <>
            <Link component="button" className={classes.helpLink} onClick={handleClick}>
                Why are some products missing?
                <HelpOutline className={classes.helpIcon} />
            </Link>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                classes={{
                    paper: classes.paper,
                }}
            >
                <Box>
                    <Typography variant="subtitle1" style={{fontWeight: 600, marginBottom: 12}}>
                        Products Not Available
                    </Typography>

                    {failureReasons.map((product) => (
                        <Box key={product.productCode} className={classes.productItem}>
                            <Typography
                                variant="body2"
                                className={classes.productName}
                                style={{fontWeight: 600, marginBottom: 4}}
                            >
                                {product.productName}
                            </Typography>
                            <Box style={{marginLeft: 12}}>
                                {product.failureReasons.map((reason, index) => (
                                    <RenderFailureReason key={index} reason={reason} indent={0} />
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Popover>
        </>
    );
};
